import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { TobaccoInvoiceDiscount, TobaccoMinimumBasis, TobaccoSettingsDto, TobaccoSettingsInput, TobaccoShortfall } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";

// Settings › Tobacco fees (migration 049, docs/tobacco-fees.md). Off by default for every business. The settings key
// opens the screen; changing it needs the business owner or the advanced accounting right (journalEntryAdd), because
// it maps the fees to a liability account and decides how sales are charged. The three open cases (minimum basis,
// a fee-inclusive price below the minimum, the invoice discount) start undecided, and the fee cannot be switched on
// until each is chosen: no financial rule is applied silently. Every change is audited with a reason.

type Account = { id: string; code: string; nameEn: string; nameAr: string };
type Form = Omit<TobaccoSettingsInput, "reason">;
const formOf = (d: TobaccoSettingsDto): Form => ({
  enabled: d.enabled, licenceNumber: d.licenceNumber, municipality: d.municipality, licenceExpiresOn: d.licenceExpiresOn, feePct: d.feePct, minimumFee: d.minimumFee,
  priceIncludesFee: d.priceIncludesFee, liabilityAccountId: d.liabilityAccountId, minimumBasis: d.minimumBasis, inclusiveShortfall: d.inclusiveShortfall, invoiceDiscount: d.invoiceDiscount
});

export function TobaccoSettings() {
  const { t, L, lang } = useLang();
  const toast = useToast();
  const [x, setX] = useState<TobaccoSettingsDto | null>(null);
  const [f, setF] = useState<Form | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [reason, setReason] = useState("");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const [d, o] = await Promise.all([api<TobaccoSettingsDto>("GET", "/settings/tobacco"), api<{ accounts: Account[]; canEdit: boolean }>("GET", "/settings/tobacco/options")]);
      setX(d); setF(formOf(d)); setAccounts(o.accounts); setCanEdit(o.canEdit); setReason(""); setErrs({});
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => { setF((o) => o && { ...o, [k]: v }); setErrs((o) => ({ ...o, [k]: "" })); };
  const dirty = !!x && !!f && JSON.stringify(formOf(x)) !== JSON.stringify(f);
  const missing = f ? [!f.liabilityAccountId && t("tobAccount"), !f.minimumBasis && t("tobDecA"), !f.inclusiveShortfall && t("tobDecB"), !f.invoiceDiscount && t("tobDecC")].filter(Boolean) as string[] : [];
  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });

  async function save() {
    if (!f) return;
    const e: Record<string, string> = {};
    if (f.licenceNumber && !/^[0-9]{1,30}$/.test(f.licenceNumber)) e.licenceNumber = t("tobDigitsOnly");
    if (f.enabled && missing.length) e.enabled = t("tobEnableNeeds", { x: missing.join(t("listSep")) });
    setErrs(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const d = await api<TobaccoSettingsDto>("PUT", "/settings/tobacco", { ...f, reason: reason.trim() });
      setX(d); setF(formOf(d)); setReason("");
      toast(t(d.enabled ? "tobSavedOn" : "tobSavedOff"));
    } catch (err) {
      setErrs(fieldErrorsOf(t, err));
      toast(errorText(t, err));
    } finally { setBusy(false); }
  }

  const radio = <K extends "minimumBasis" | "inclusiveShortfall" | "invoiceDiscount">(k: K, options: [NonNullable<Form[K]>, string, string][]) => (
    <div className="tob-choices" role="radiogroup" data-testid={`tob-${k}`}>
      {options.map(([v, label, example]) => (
        <label key={String(v)} className={`tob-choice ${f?.[k] === v ? "on" : ""}`}>
          <input type="radio" name={k} checked={f?.[k] === v} disabled={!canEdit} onChange={() => set(k, v as Form[K])} data-testid={`tob-${k}-${v}`} />
          <span><b>{label}</b><small className="hint">{example}</small></span>
        </label>
      ))}
      {f?.[k] === null && <span className="pill warn" data-testid={`tob-${k}-open`}>{t("tobUndecided")}</span>}
      {errs[k] && <div className="ferr" role="alert">{errs[k]}</div>}
    </div>
  );

  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !x || !f ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      {!canEdit && <div className="dep-note" data-testid="tob-locked"><Icon name="lock" /><span>{t("tobLocked")}</span></div>}
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("tobStatus")}</h2><p>{t("tobIntro")}</p></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className={`field full ${errs.enabled ? "invalid" : ""}`}>
            <label className="check-row"><input type="checkbox" checked={f.enabled} disabled={!canEdit} onChange={(e) => set("enabled", e.target.checked)} data-testid="tob-enabled" /> {t("tobEnable")}</label>
            <div className="ferr" role="alert" data-testid="tob-enabled-err">{errs.enabled}</div>
            <span className="hint">{t(x.enabled ? "tobOnHint" : "tobOffHint")}</span>
          </div>
          <div className="field full" data-testid="tob-state">
            <span className="label">{t("status")}</span>
            <span>{x.enabled ? <span className="pill good"><Icon name="check" />{t("tobOn")}</span> : <span className="pill">{t("tobOff")}</span>}
              <span className="hint"> {t("tobItems", { n: String(x.itemsCount) })} · <Link to="/items/allItems">{t("tobItemsLink")}</Link></span></span>
            {x.updatedAt && <span className="hint">{t("tobUpdated", { u: x.updatedByName ?? "", at: when(x.updatedAt) })}</span>}
          </div>
        </div></div>
      </section>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("tobLicence")}</h2><p>{t("tobLicenceHint")}</p></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className={`field ${errs.licenceNumber ? "invalid" : ""}`}>
            <label htmlFor="tob-lic">{t("tobLicenceNo")}</label>
            <input id="tob-lic" className="control num" dir="ltr" inputMode="numeric" maxLength={30} value={f.licenceNumber} disabled={!canEdit} onChange={(e) => set("licenceNumber", e.target.value)} data-testid="tob-licence" />
            <div className="ferr" role="alert">{errs.licenceNumber}</div><span className="hint">{t("tobLicenceNoHint")}</span>
          </div>
          <div className="field">
            <label htmlFor="tob-mun">{t("tobMunicipality")}</label>
            <input id="tob-mun" className="control" maxLength={120} value={f.municipality} disabled={!canEdit} onChange={(e) => set("municipality", e.target.value)} />
          </div>
          <div className={`field ${errs.licenceExpiresOn ? "invalid" : ""}`}>
            <label htmlFor="tob-exp">{t("tobExpires")}</label>
            <input id="tob-exp" className="control num" type="date" dir="ltr" value={f.licenceExpiresOn ?? ""} disabled={!canEdit} onChange={(e) => set("licenceExpiresOn", e.target.value || null)} />
            <div className="ferr" role="alert">{errs.licenceExpiresOn}</div>
          </div>
        </div></div>
      </section>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("tobCalc")}</h2><p>{t("tobCalcHint")}</p></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className={`field ${errs.feePct ? "invalid" : ""}`}>
            <label htmlFor="tob-pct">{t("tobPct")}</label>
            <div className="input-affix"><input id="tob-pct" className="control num" dir="ltr" inputMode="decimal" value={f.feePct} disabled={!canEdit} onChange={(e) => set("feePct", e.target.value)} data-testid="tob-pct" /><span className="affix">%</span></div>
            <div className="ferr" role="alert">{errs.feePct}</div>
          </div>
          <div className={`field ${errs.minimumFee ? "invalid" : ""}`}>
            <label htmlFor="tob-min">{t("tobMinimum")}</label>
            <div className="input-affix"><input id="tob-min" className="control num" dir="ltr" inputMode="decimal" value={f.minimumFee} disabled={!canEdit} onChange={(e) => set("minimumFee", e.target.value)} data-testid="tob-min" /><span className="affix">{t("currency")}</span></div>
            <div className="ferr" role="alert">{errs.minimumFee}</div>
          </div>
          <div className="dep-note full" data-testid="tob-defaults"><Icon name="info" /><span>{t("tobDefaultsNote")}</span></div>
          <div className="field full">
            <label className="check-row"><input type="checkbox" checked={f.priceIncludesFee} disabled={!canEdit} onChange={(e) => set("priceIncludesFee", e.target.checked)} data-testid="tob-incl" /> {t("tobPriceIncl")}</label>
            <span className="hint">{t("tobPriceInclHint")}</span>
          </div>
          <div className={`field span-2 ${errs.liabilityAccountId ? "invalid" : ""}`}>
            <label htmlFor="tob-acc">{t("tobAccount")}</label>
            <select id="tob-acc" className="control" value={f.liabilityAccountId ?? ""} disabled={!canEdit} onChange={(e) => set("liabilityAccountId", e.target.value || null)} data-testid="tob-account">
              <option value="">{t("tobAccountNone")}</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {L(a)}</option>)}
            </select>
            <div className="ferr" role="alert">{errs.liabilityAccountId}</div><span className="hint">{t("tobAccountHint")}</span>
          </div>
        </div></div>
      </section>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("tobDecisions")}</h2><p>{t("tobDecisionsHint")}</p></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className="field full"><span className="label">{t("tobDecA")}</span>
            {radio("minimumBasis", [["base_unit" as TobaccoMinimumBasis, t("tobA1"), t("tobA1Ex")], ["selling_unit" as TobaccoMinimumBasis, t("tobA2"), t("tobA2Ex")]])}</div>
          <div className="field full"><span className="label">{t("tobDecB")}</span>
            {radio("inclusiveShortfall", [["refuse" as TobaccoShortfall, t("tobB1"), t("tobB1Ex")], ["minimum_on_top" as TobaccoShortfall, t("tobB2"), t("tobB2Ex")]])}</div>
          <div className="field full"><span className="label">{t("tobDecC")}</span>
            {radio("invoiceDiscount", [["all_lines" as TobaccoInvoiceDiscount, t("tobC1"), t("tobC1Ex")], ["exclude_fee_lines" as TobaccoInvoiceDiscount, t("tobC2"), t("tobC2Ex")]])}</div>
        </div></div>
      </section>
      {canEdit && <section className="set-sec">
        <div className="set-sec-h"><h2>{t("tobReason")}</h2></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className="field full"><label htmlFor="tob-reason">{t("tobReason")}</label>
            <input id="tob-reason" className="control" maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="tob-reason" /><span className="hint">{t("tobReasonHint")}</span></div>
          <div className="dep-note full"><Icon name="info" /><span>{t("tobNoZatcaClaim")}</span></div>
        </div></div>
      </section>}
    </div>
  );

  const bar = x && f && canEdit && (
    <div className="action-bar">
      <div className="ab-stats"><div><div className="k">{t("status")}</div><div className="v" data-testid="form-state">{dirty ? <span className="pill warn">{t("unsavedChanges")}</span> : <span className="pill good">{t("allSaved")}</span>}</div></div></div>
      <div className="ab-actions">
        <button className="btn" disabled={!dirty || busy} onClick={() => { setF(formOf(x)); setErrs({}); }}>{t("discardChanges")}</button>
        <button className="btn btn-primary" data-testid="tob-save" disabled={!dirty || busy} onClick={() => void save()}><Icon name="check" />{busy ? t("saving") : t("save")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/settings/tobacco" bar={bar || undefined}>
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("tobSettings")]]} title={t("tobSettings")} />
      {body}
    </Shell>
  );
}
